import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import classnames from 'classnames';

import Button from '@z-components/atoms/Button';
import type { VerticalPageJobType } from 'modules/job-types/types';

import { openNewRequestModal } from 'modules/requests/service';
import { CTATypes, modifyUtmParamsWithCTAType, Utm } from 'utils/internal-blog';
import { setItem } from 'utils/webStorage';
import { isUserAuthenticated, logEvent } from 'utils/analytics';

import styles from './InternalBlogPage.module.scss';

const DEBOUNCE_TIME = 300;
const SCROLL_HEIGHT_BEFORE_FIXED_CTA_DISAPPEAR = 1200;

interface Props {
  dataGtmClick?: string;
  internalBlogUtmParams: Utm | null;
  jobType: VerticalPageJobType;
  sectionButtonTranslationId: string;
  sectionTitleTranslationId: string;
}

const InternalBlogStickyCTASection: FC<Props> = ({
  dataGtmClick,
  internalBlogUtmParams,
  jobType,
  sectionButtonTranslationId,
  sectionTitleTranslationId,
}) => {
  const [isShown, setVisibility] = useState(false);
  const [isHideButtonClicked, setIsHideButtonClicked] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleCTAButtonClick = useCallback(() => {
    if (internalBlogUtmParams) {
      setItem(
        'ngStorage-utm',
        JSON.stringify(modifyUtmParamsWithCTAType(internalBlogUtmParams, CTATypes.following))
      );
    }
    logEvent(
      `NewRequest:${jobType.id}`,
      'startNewRequest:followingCTAClicked',
      isUserAuthenticated() ? 'authenticated' : 'unauthenticated'
    );

    openNewRequestModal({
      dispatch,
      properties: { jobTypeId: jobType.id },
    });
  }, [dispatch, internalBlogUtmParams, jobType.id]);

  const handleHideButtonClick = () => {
    setIsHideButtonClicked(true);
    setVisibility(false);
  };

  useEffect(() => {
    const subscription = fromEvent(window, 'scroll')
      .pipe(debounceTime(DEBOUNCE_TIME))
      .subscribe(() => {
        setVisibility(
          !isHideButtonClicked && window.scrollY >= SCROLL_HEIGHT_BEFORE_FIXED_CTA_DISAPPEAR
        );
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [isHideButtonClicked]);

  return (
    <div className={styles['sticky-cta-section']}>
      {isShown && (
        <div className={styles['sticky-cta-container']}>
          <div className={styles['cta-form-large-button-layout']}>
            <Button
              onClick={handleHideButtonClick}
              className={styles['sticky-cta-hide-section-button']}
              text="✕"
            />
            <div className={styles['cta-form-text-and-button-container']}>
              <p>{t(sectionTitleTranslationId, { jobType: jobType.translations.requestType })}</p>
              <Button
                className={classnames(styles['sticky-cta-form-button'], {
                  [styles['animate-button']]: true,
                })}
                dataGtmClick={dataGtmClick}
                onClick={handleCTAButtonClick}
                text={t(sectionButtonTranslationId)}
                id="following-CTA"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InternalBlogStickyCTASection;
